import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Save, Check } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';

// Only show editing tools on the Readdy editor, not on the live site
const isEditMode = typeof window !== 'undefined' &&
  (window.location.hostname.includes('readdy.ai') ||
   window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

interface Artwork {
  id: number;
  title: string;
  artist: string;
  year: string;
  medium: string;
  dimensions: string;
  imageUrl: string;
  description: string;
  location: string;
  sort_order: number;
}

interface InformationOverlayProps {
  artworkId: number;
  artworks: Artwork[];
  onClose: () => void;
  onNavigate: (id: number) => void;
  onSaved?: (updated: Artwork) => void;
}

export default function InformationOverlay({
  artworkId,
  artworks,
  onClose,
  onNavigate,
  onSaved,
}: InformationOverlayProps) {
  const currentIndex = artworks.findIndex((a) => a.id === artworkId);
  const artwork = artworks[currentIndex];

  const [form, setForm] = useState<Artwork>({ ...artwork });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!artwork) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < artworks.length - 1;

  const handlePrev = () => {
    if (hasPrev) onNavigate(artworks[currentIndex - 1].id);
  };

  const handleNext = () => {
    if (hasNext) onNavigate(artworks[currentIndex + 1].id);
  };

  const handleChange = (field: keyof Artwork, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const payload = {
        title: form.title,
        artist: form.artist,
        year: form.year,
        medium: form.medium,
        dimensions: form.dimensions,
        description: form.description,
        location: form.location,
        image_url: form.imageUrl,
      };

      const { data, error: sbError } = await supabase
        .from('artworks')
        .update(payload)
        .eq('id', form.id)
        .select()
        .maybeSingle();

      if (sbError) {
        setError(`Save failed: ${sbError.message} (code: ${sbError.code})`);
      } else if (!data) {
        setError('No matching artwork found to update. Check the ID.');
      } else {
        setSaved(true);
        onSaved?.(form);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Unexpected error: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const isDirty = JSON.stringify(form) !== JSON.stringify(artwork);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
          <div>
            <h2 className="text-base font-medium text-black">Artwork Details</h2>
            {artworks.length > 1 && (
              <p className="text-xs text-black/40 mt-0.5">{currentIndex + 1} / {artworks.length}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Image */}
        <div className="px-6 pt-5 pb-2">
          <div className="w-full h-48 bg-stone-50 rounded-lg overflow-hidden flex items-center justify-center border border-black/6">
            <img
              src={form.imageUrl}
              alt={form.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>

        {/* Fields */}
        <div className="px-6 pb-4 space-y-4 mt-2">

          {isEditMode ? (
            /* ── EDITOR: editable fields ── */
            <>
              <div>
                <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => handleChange('title', e.target.value)}
                  className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Artist</label>
                <input
                  type="text"
                  value={form.artist}
                  onChange={e => handleChange('artist', e.target.value)}
                  className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Year</label>
                  <input
                    type="text"
                    value={form.year}
                    onChange={e => handleChange('year', e.target.value)}
                    className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Medium</label>
                  <input
                    type="text"
                    value={form.medium}
                    onChange={e => handleChange('medium', e.target.value)}
                    className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Dimensions</label>
                <input
                  type="text"
                  value={form.dimensions}
                  onChange={e => handleChange('dimensions', e.target.value)}
                  className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-black/50 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => handleChange('description', e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors resize-none"
                />
                <p className="text-xs text-black/30 mt-1 text-right">{form.description.length}/500</p>
              </div>

              {/* Save Button */}
              <div className="pt-1">
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap
                    ${saved
                      ? 'bg-green-500 text-white'
                      : isDirty
                        ? 'bg-black text-white hover:bg-stone-800'
                        : 'bg-black/8 text-black/30 cursor-not-allowed'
                    }
                    disabled:opacity-60`}
                >
                  {saving ? (
                    <><i className="ri-loader-4-line animate-spin text-base"></i> Saving…</>
                  ) : saved ? (
                    <><Check className="w-4 h-4" /> Saved!</>
                  ) : (
                    <><Save className="w-4 h-4" /> Save Changes</>
                  )}
                </button>

                {error && (
                  <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* ── LIVE SITE: read-only display ── */
            <div className="space-y-5">
              <div>
                <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Title</p>
                <p className="text-lg font-light text-black" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{artwork.title}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Artist</p>
                <p className="text-sm text-black/80">{artwork.artist}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Year</p>
                  <p className="text-sm text-black/80">{artwork.year}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Medium</p>
                  <p className="text-sm text-black/80">{artwork.medium}</p>
                </div>
              </div>

              {artwork.dimensions && (
                <div>
                  <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Dimensions</p>
                  <p className="text-sm text-black/80">{artwork.dimensions}</p>
                </div>
              )}

              {artwork.description && (
                <div>
                  <p className="text-xs font-medium text-black/40 uppercase tracking-wide mb-1">Description</p>
                  <p className="text-sm text-black/60 leading-relaxed font-light">{artwork.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        {artworks.length > 1 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between rounded-b-xl">
            <button
              onClick={handlePrev}
              disabled={!hasPrev}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={handleNext}
              disabled={!hasNext}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
